import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { DeleteTeams$Request, DivisionId, CompanyId } from '../../../../services/api.types'
import { red } from '../../../../styles/material-ui-theme-v2'
import { Dialog as CapacitorDialog } from '@capacitor/dialog'
import { t } from 'ttag'
import YumButton from '../../../../components/YumButton/YumButton'
import { ReactComponent as RemoveIcon } from '../../../../assets/v3/icons/remove.svg'
import { IGlobalState } from '../../../../Root/store/appReducer'
import { getDeleteTeamsLoading, getLastTeamsUpdate } from '../../store/TeamManagementSelectors'
import { deleteTeams } from '../../store/TeamManagementActions'
import { isRtl } from '../../../../i18nInit'

export interface ICreateTeam {
  text: string
  divisionId: DivisionId
  companyId: CompanyId
  members: number | undefined
  isDuringRegistration?: boolean
  style?: object
}

export interface ICreateTeamDispatchProps {
  deleteTeams(payload: DeleteTeams$Request & { companyId?: CompanyId; silentSuccess: boolean }): void
}

export interface ICreateTeamStateProps {
  deleteTeamsLoading: CompanyId | null
  lastTeamsUpdate: number | null
}

export function DeleteTeam(props: ICreateTeam & ICreateTeamDispatchProps & ICreateTeamStateProps) {
  const {
    text,
    divisionId,
    companyId,
    members,
    isDuringRegistration,
    style,
    deleteTeams,
    deleteTeamsLoading,
    lastTeamsUpdate,
  } = props
  // Track if we've sent the delete request
  const [hasSentRequest, setHasSentRequest] = useState(false)
  // Store the lastTeamsUpdate value when we send the request
  const [requestTimestamp, setRequestTimestamp] = useState<number | null>(null)

  // Reset hasSentRequest when lastTeamsUpdate changes after we've sent a request
  useEffect(() => {
    if (hasSentRequest && lastTeamsUpdate !== null && requestTimestamp !== lastTeamsUpdate) {
      setHasSentRequest(false)
      setRequestTimestamp(null)
    }
  }, [lastTeamsUpdate, hasSentRequest, requestTimestamp])

  const handleDeleteTeam = async () => {
    // Check if a team in this company is already being deleted
    const isTeamBeingDeleted = deleteTeamsLoading === companyId

    // Prevent action if already deleting or if we've sent the request
    if (isTeamBeingDeleted || hasSentRequest) return

    if (members && members > 1) {
      const x = members
      const { value } = await CapacitorDialog.confirm({
        title: t`Confirm`,
        message: t`Are you sure? ${x} users will lose their team.`,
      })
      if (!value) return
    }

    // Mark that we've sent the request and store current lastTeamsUpdate
    setHasSentRequest(true)
    setRequestTimestamp(lastTeamsUpdate)

    // Call the delete action with the companyId
    deleteTeams({
      divisionIds: [divisionId],
      companyId: companyId,
      silentSuccess: !!isDuringRegistration,
    })
  }

  // Check if the company that this team belongs to is being deleted
  const isTeamBeingDeleted = deleteTeamsLoading === companyId

  // Consider the button disabled if we're loading or have sent a request
  const isDisabled = isTeamBeingDeleted || hasSentRequest

  return isDuringRegistration ? (
    <YumButton
      onClick={handleDeleteTeam}
      variant="text"
      size="small"
      disabled={isDisabled}
      style={{
        display: 'block',
        color: red,
        padding: '10px 12px',
        margin: '6px 0 0',
        fontWeight: 400,
        height: 'auto',
        fontSize: '1rem',
        lineHeight: '24px',
        opacity: isDisabled ? 0.7 : 1, // Reduce opacity when deleting
        ...style,
      }}
    >
      <RemoveIcon style={{ marginRight: isRtl ? '0px' : '8px', marginLeft: !isRtl ? '0px' : '8px' }} />
      {isDisabled ? t`Deleting...` : text}
    </YumButton>
  ) : (
    <a
      href={undefined}
      onClick={isDisabled ? undefined : handleDeleteTeam}
      style={{
        color: red,
        textDecoration: 'none',
        marginTop: '5px',
        display: 'inline-block',
        cursor: isDisabled ? 'default' : 'pointer',
        opacity: isDisabled ? 0.7 : 1,
      }}
    >
      {isDisabled ? t`Deleting...` : text}
    </a>
  )
}

const mapStateToProps = (state: IGlobalState): ICreateTeamStateProps => ({
  deleteTeamsLoading: getDeleteTeamsLoading(state),
  lastTeamsUpdate: getLastTeamsUpdate(state),
})

const mapDispatchToProps: ICreateTeamDispatchProps = {
  deleteTeams,
}

export default connect(mapStateToProps, mapDispatchToProps)(DeleteTeam)
